import { type ReactNode } from 'react'

import cn from '@components/utils/cn'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { useEventTimeZone } from '../author_shifts/ShiftAuthoringContext'

import type { AssigneeRefFields, ChangeBreakdownFields } from '../shifts/types'

const t = scopedTranslation('roster_board')

const nameList = new Intl.ListFormat('en', { style: 'narrow', type: 'unit' })

function fieldLabel(key: string): string {
  return t(`changes.field_${key.toLowerCase()}`, { defaultValue: '' })
}

export function changeCount(breakdown: ChangeBreakdownFields): number {
  return breakdown.fieldChanges.length + breakdown.addedAssignees.length + breakdown.removedAssignees.length
}

function formatValue(key: string, value: string | null | undefined, zone?: string | null): string {
  if (value == null || value === '') return ''

  switch (key) {
    case 'START_TIME':
    case 'END_TIME':
      return timeFormatter.fromISO(value, zone).toHoursMinutes()
    case 'DATE':
      return timeFormatter.fromISO(value, zone).toShortDate()
    case 'ALLOW_SELF_SELECT':
      return value === 'true' ? t('changes.access_on') : t('changes.access_off')
    default:
      return value
  }
}

function formatNames(assignees: AssigneeRefFields[]): string {
  return nameList.format(assignees.map((assignee) => assignee.fullName ?? t('changes.unknown_volunteer')))
}

type Tone = 'edit' | 'add' | 'remove'

const TONE_COLOR: Record<Tone, string> = {
  edit: 'tw:text-warning-500',
  add: 'tw:text-success-500',
  remove: 'tw:text-error-500',
}

function Marker({ tone, showLine }: { tone: Tone; showLine: boolean }) {
  return (
    <span
      className={cn('tw:flex tw:w-1.5 tw:shrink-0 tw:flex-col tw:items-center tw:self-stretch', TONE_COLOR[tone])}
    >
      <span className="tw:mt-1 tw:h-1.5 tw:w-1.5 tw:shrink-0 tw:rounded-full tw:bg-current" aria-hidden />
      {showLine && <span className="tw:mt-0.5 tw:w-0.5 tw:flex-1 tw:rounded tw:bg-current tw:opacity-40" aria-hidden />}
    </span>
  )
}

function LedgerRow({ tone, label, showLine, children }: { tone: Tone; label: string; showLine: boolean; children: ReactNode }) {
  return (
    <li className="tw:flex tw:gap-2.5 tw:py-1 tw:text-xs">
      <Marker tone={tone} showLine={showLine} />
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-start tw:gap-2">
        <span className="tw:w-16 tw:shrink-0 tw:text-gray-500">{label}</span>
        <span className="tw:min-w-0 tw:text-gray-900">{children}</span>
      </div>
    </li>
  )
}

type LedgerEntry = { key: string; tone: Tone; label: string; content: ReactNode }

export function ChangeBreakdown({
  breakdown,
  showTitle = true,
}: {
  breakdown: ChangeBreakdownFields
  showTitle?: boolean
}) {
  const { fieldChanges, addedAssignees, removedAssignees } = breakdown
  const count = changeCount(breakdown)
  const timeZone = useEventTimeZone()

  const entries: LedgerEntry[] = [
    ...fieldChanges.map((change) => ({
      key: change.key,
      tone: 'edit' as Tone,
      label: fieldLabel(change.key),
      content: (
        <>
          <span className="tw:text-gray-500 tw:line-through">{formatValue(change.key, change.from, timeZone)}</span>{' '}
          <span className="tw:text-gray-500">&rarr;</span> {formatValue(change.key, change.to, timeZone)}
        </>
      ),
    })),
    ...(addedAssignees.length > 0
      ? [{ key: 'added', tone: 'add' as Tone, label: t('changes.added_label'), content: formatNames(addedAssignees) }]
      : []),
    ...(removedAssignees.length > 0
      ? [{ key: 'removed', tone: 'remove' as Tone, label: t('changes.removed_label'), content: formatNames(removedAssignees) }]
      : []),
  ]

  return (
    <div className="tw:flex tw:flex-col">
      {showTitle && (
        <p className="tw:mb-2 tw:border-b tw:border-gray-100 tw:pb-2 tw:text-xs tw:font-bold tw:text-gray-900">
          {t('changes.title', { count })}
        </p>
      )}
      <ul className="tw:flex tw:flex-col">
        {entries.map((entry, index) => (
          <LedgerRow key={entry.key} tone={entry.tone} label={entry.label} showLine={index < entries.length - 1}>
            {entry.content}
          </LedgerRow>
        ))}
      </ul>
    </div>
  )
}
